// PlatformKeyGuard — site-wide voucher gate.
//
// May 25, 2026. Any time a user is about to engage an LLM surface that
// will tap the platform provider key (Claude / Anthropic, OpenAI / GPT,
// or Sora / Whisper), we need to verify the target silo's voucher can
// cover at least $1 of spend. BYOK is GEMINI-ONLY in this codebase (see
// `adjust_key_for_provider` in server.py) — so selecting any non-Gemini
// model forces platform-key usage and a voucher debit.
//
// The guard is a pop-up that:
//   1. Fetches the silo's voucher balance + has_byok status
//   2. If voucher < $1 AND the surface uses a platform-bound provider,
//      offers two actions: "Pick a different model" (cancel) or
//      "Top up [silo]" (opens the existing VoucherTopupModal).
//
// Usage:
//   const guard = usePlatformKeyGuard();
//   guard.checkBeforeModelChange({
//     siloId: silo.id,
//     newProvider: "anthropic",   // anthropic | openai | gemini | sora | whisper
//     minDollarsNeeded: 1,        // default 1
//     onConfirmed: () => save(),  // called when ok to proceed
//   });

import { useState, useCallback } from "react";
import { X, Lightning } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import VoucherTopupModal from "@/components/VoucherTopupModal";

// Providers that ALWAYS use the platform key (BYOK is Gemini-only)
const PLATFORM_BOUND_PROVIDERS = new Set(["anthropic", "openai", "sora", "whisper"]);

export function isPlatformBoundProvider(provider) {
  return PLATFORM_BOUND_PROVIDERS.has((provider || "").toLowerCase());
}

export function usePlatformKeyGuard() {
  const [gate, setGate] = useState(null); // { siloId, siloName, voucherCents, hasByok, minDollarsNeeded, onConfirmed, providerLabel }
  const [topupSiloId, setTopupSiloId] = useState(null);
  const [checking, setChecking] = useState(false);

  const checkBeforeModelChange = useCallback(async ({ siloId, newProvider, minDollarsNeeded = 1, providerLabel = null, onConfirmed }) => {
    // If the new provider isn't platform-bound (e.g., switching TO
    // Gemini), there's nothing to gate — proceed.
    if (!isPlatformBoundProvider(newProvider)) {
      onConfirmed && onConfirmed();
      return;
    }
    // No siloId means we can't check a voucher — proceed.
    if (!siloId) {
      onConfirmed && onConfirmed();
      return;
    }
    setChecking(true);
    try {
      const r = await api.get(`/silos/${siloId}/boost-context`);
      const ctx = r.data || {};
      const minCents = Math.max(100, Math.round(minDollarsNeeded * 100));
      if ((ctx.voucher_cents || 0) >= minCents) {
        // Enough headroom — proceed.
        onConfirmed && onConfirmed();
        return;
      }
      // Voucher too low — open the gate dialog.
      setGate({
        siloId,
        siloName: ctx.silo_name || "this silo",
        voucherCents: ctx.voucher_cents || 0,
        hasByok: !!ctx.has_byok,
        minDollarsNeeded,
        providerLabel: providerLabel || newProvider,
        onConfirmed,
      });
    } catch {
      // If the check fails for any reason, let the action through — don't
      // block on infrastructure flakiness. The server-side voucher guard
      // still enforces the floor on actual calls.
      onConfirmed && onConfirmed();
    } finally {
      setChecking(false);
    }
  }, []);

  const closeGate = () => setGate(null);

  const overlay = gate ? (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={closeGate}
      data-testid="platform-key-guard-backdrop"
    >
      <div
        className="bg-white rounded-lg max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="platform-key-guard-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A5200] flex items-center gap-1.5">
              <Lightning size={12} weight="duotone" /> Platform key guard
            </div>
            <h3 className="font-serif-display text-xl text-[#1C1B1A] mt-1">Voucher too low</h3>
          </div>
          <button onClick={closeGate} className="text-[#6B6A68] hover:text-[#1C1B1A] p-1" data-testid="platform-key-guard-close">
            <X size={16} />
          </button>
        </div>

        <p className="text-sm text-[#1C1B1A] leading-relaxed mb-2">
          <strong>{gate.providerLabel}</strong> always runs on the platform provider key — BYOK is Gemini-only.
        </p>
        <p className="text-xs text-[#6B6A68] leading-relaxed mb-3">
          To switch this surface to {gate.providerLabel}, <strong>{gate.siloName}</strong> needs at least <strong>${gate.minDollarsNeeded.toFixed(2)}</strong> in its voucher. Currently: <strong className="tabular-nums">${(gate.voucherCents / 100).toFixed(2)}</strong>.
        </p>

        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <button
            type="button"
            onClick={closeGate}
            data-testid="platform-key-guard-cancel"
            className="px-3 py-1.5 rounded-md border hairline text-xs uppercase tracking-[0.15em] text-[#4A5D4E] hover:bg-[#F0F1EE]"
          >
            Pick a different model
          </button>
          <button
            type="button"
            onClick={() => { setTopupSiloId(gate.siloId); }}
            data-testid="platform-key-guard-topup"
            className="px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#1C1B1A]"
          >
            Top up {gate.siloName}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const topupModal = (
    <VoucherTopupModal
      open={!!topupSiloId}
      prefocusedSiloId={topupSiloId}
      onClose={() => setTopupSiloId(null)}
      onSuccess={async () => {
        // Top-up succeeded — re-check voucher, and if it now satisfies
        // the min, dismiss the gate AND fire the deferred callback.
        if (gate?.siloId) {
          try {
            const r = await api.get(`/silos/${gate.siloId}/boost-context`);
            const ok = (r.data?.voucher_cents || 0) >= Math.max(100, Math.round(gate.minDollarsNeeded * 100));
            if (ok) {
              const cb = gate.onConfirmed;
              setGate(null);
              setTopupSiloId(null);
              cb && cb();
              return;
            }
          } catch { /* no-op */ }
        }
        setTopupSiloId(null);
      }}
    />
  );

  return {
    checkBeforeModelChange,
    checking,
    overlay: <>{overlay}{topupModal}</>,
  };
}
